import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, mergeMap, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { NotificationsService } from '@shared/services/notifications.service';
import { WithdrawalRequestsWithPagination } from '../../models/api/response/user-management.response';
import { BadResponseWithError } from '@shared/models/common.interface';
import { WithdrawalsAction } from '../actions/actions-type.action';
import { WithdrawalRequest } from '../../models/api/requests/user-management.request';
import { WithdrawalRequestStatusType } from '../../models/withdrawals.interface';
import { WithdrawalsService } from '../../services/withdrawals.service';
import { addHashToWithdrawalRequests } from '../actions/withdrawals.action';
import { CommonService } from '@shared/services/common.service';

@Injectable({ providedIn: 'root' })
export class WithdrawalsEffect {
    fetchProfilePaymentAccountfetchWithdrawalRequests$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WithdrawalsAction.fetchWithdrawalRequests),
            switchMap(({ payload }) =>
                this.withdrawalsService
                    .fetchWithdrawalRequests(
                        payload?.status,
                        payload?.paymentMethod,
                        payload?.network,
                        payload?.giftCardProvider
                    )
                    .pipe(
                        map((items: WithdrawalRequestsWithPagination) =>
                            WithdrawalsAction.fetchWithdrawalRequestsSuccess({
                                payload: {
                                    items,
                                },
                            })
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                WithdrawalsAction.fetchWithdrawalRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    changeWithdrawalRequest = createEffect(() =>
        this.actions$.pipe(
            ofType(WithdrawalsAction.changeWithdrawalRequestStatus),
            switchMap(({ payload }) =>
                this.withdrawalsService
                    .changeWithdrawalRequestStatus(
                        payload.profileId,
                        payload.requestId,
                        payload.status
                    )
                    .pipe(
                        mergeMap((request: WithdrawalRequest) => [
                            WithdrawalsAction.changeWithdrawalRequestStatusSuccess(
                                {
                                    payload: {
                                        request,
                                    },
                                }
                            ),
                            WithdrawalsAction.fetchWithdrawalRequests({
                                payload: {
                                    status: WithdrawalRequestStatusType.Pending,
                                    paymentMethod:
                                        payload.filters.paymentMethod,
                                    network: payload.filters.network,
                                    giftCardProvider:
                                        payload.filters.giftCardProvider,
                                },
                            }),
                        ]),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                WithdrawalsAction.changeWithdrawalRequestStatusFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    addHashToWithdrawalRequests$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WithdrawalsAction.addHashToWithdrawalRequests),
            switchMap(({ payload }) =>
                this.withdrawalsService
                    .addHashIdToWithdrawallRequest(
                        payload.cryptoHashId,
                        payload.withdrawalId
                    )
                    .pipe(
                        map(() =>
                            WithdrawalsAction.addHashToWithdrawalRequestsSuccess(
                                {
                                    payload: {
                                        withdrawalId: payload.withdrawalId,
                                        cryptoHashId: payload.cryptoHashId,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.showError(
                                response.error.message
                            );

                            return of(
                                WithdrawalsAction.addHashToWithdrawalRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    downloadFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WithdrawalsAction.downloadWithdrawal),
            switchMap(({ payload }) =>
                this.commonService.downloadFile(payload.url).pipe(
                    map((blob: Blob) => {
                        let url = window.URL.createObjectURL(blob);
                        let a = document.createElement('a');
                        document.body.appendChild(a);
                        a.setAttribute('style', 'display: none');
                        a.href = url;
                        a.download = payload.fileName;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();

                        this.notificationsService.showSuccessMessage(
                            'File generated.'
                        );

                        return WithdrawalsAction.downloadFileSuccess();
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.showError(
                            response.error?.message
                        );

                        return of(
                            WithdrawalsAction.downloadFileFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private notificationsService: NotificationsService,
        private withdrawalsService: WithdrawalsService,
        private commonService: CommonService
    ) {}
}
